import { useMemo } from "react";
import { useActiveCompany } from "@/contexts/ActiveCompanyContext";
import { UserCompanyRole } from "@/hooks/useUserCompanies";
import { useAuthContext } from "@/contexts/AuthContext";
import { useIsSuperAdmin } from "./useIsSuperAdmin";

export interface CompanyPermissions {
  // High-level permissions
  canManageCompany: boolean;
  canManageMembers: boolean;
  canPostJobs: boolean;
  canViewApplications: boolean;
  canManageLocations: boolean;
  canManageDepartments: boolean;
  canInviteMembers: boolean;
  canEditCompanyProfile: boolean;

  // Role checks
  roles: UserCompanyRole[];
  isOwner: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isRecruiter: boolean;
  isMember: boolean;

  // Scope checks
  hasLocationScope: boolean;
  hasDepartmentScope: boolean;
  locationIds: string[];
  departmentIds: string[];
}

export function useCompanyPermissions(): CompanyPermissions {
  const { activeCompany } = useActiveCompany();
  const { user } = useAuthContext();
  const { data: isSuperAdmin } = useIsSuperAdmin();

  return useMemo(() => {
    // Super admins have ALL permissions for ALL companies
    if (isSuperAdmin) {
      return {
        canManageCompany: true,
        canManageMembers: true,
        canPostJobs: true,
        canViewApplications: true,
        canManageLocations: true,
        canManageDepartments: true,
        canInviteMembers: true,
        canEditCompanyProfile: true,
        roles: [{ role: "admin", scope_type: "company", scope_id: null }],
        isOwner: false,
        isAdmin: true,
        isManager: false,
        isRecruiter: false,
        isMember: false,
        hasLocationScope: false,
        hasDepartmentScope: false,
        locationIds: [],
        departmentIds: [],
      };
    }

    if (!activeCompany) {
      // No active company - return empty permissions
      return {
        canManageCompany: false,
        canManageMembers: false,
        canPostJobs: false,
        canViewApplications: false,
        canManageLocations: false,
        canManageDepartments: false,
        canInviteMembers: false,
        canEditCompanyProfile: false,
        roles: [],
        isOwner: false,
        isAdmin: false,
        isManager: false,
        isRecruiter: false,
        isMember: false,
        hasLocationScope: false,
        hasDepartmentScope: false,
        locationIds: [],
        departmentIds: [],
      };
    }

    const roles = activeCompany.roles || [];
    const roleNames = roles.map((r) => r.role);

    const isOwner = roleNames.includes("owner");
    const isAdmin = roleNames.includes("admin");
    const isManager = roleNames.includes("manager");
    const isRecruiter = roleNames.includes("recruiter");
    const isMember = roleNames.includes("member");

    // Extract scoped roles
    const locationScopes = roles.filter((r) => r.scope_type === "location" && r.scope_id);
    const departmentScopes = roles.filter((r) => r.scope_type === "department" && r.scope_id);

    return {
      // Permissions derived from role hierarchy
      canManageCompany: isOwner,
      canManageMembers: isOwner || isAdmin,
      canPostJobs: isOwner || isAdmin || isRecruiter,
      canViewApplications: isOwner || isAdmin || isRecruiter || isManager,
      canManageLocations: isOwner || isAdmin,
      canManageDepartments: isOwner || isAdmin,
      canInviteMembers: isOwner || isAdmin,
      canEditCompanyProfile: isOwner || isAdmin,

      // Role checks
      roles,
      isOwner,
      isAdmin,
      isManager,
      isRecruiter,
      isMember,

      // Scope data
      hasLocationScope: locationScopes.length > 0,
      hasDepartmentScope: departmentScopes.length > 0,
      locationIds: locationScopes.map((r) => r.scope_id!),
      departmentIds: departmentScopes.map((r) => r.scope_id!),
    };
  }, [activeCompany, isSuperAdmin]);
}
